import * as React from 'react';
import type {
  EntityDecision,
  LinkData,
  MediaPlanPreviewNotice,
  ResolvedEntity
} from 'shared/src/api/ai-media-plan-chat-types';
import type {
  CreateMetaAdSetPayload,
  CreateMetaCampaignPayload,
  MetaGeoLocations
} from 'shared/src/meta/meta-types';
import { buildMetaEntityUrl } from 'shared/src/domain/ai/skills/meta/shared-config';
import {
  extractPartialFailureDetail,
  formatPartialFailureMessage
} from 'shared/src/api/partial-failure';
import { Button } from '@/components/forms/ui/button';
import { Input } from '@/components/forms/ui/input';
import { trpc } from '@/utils/trpc-client';
import { showErrorToast } from '@/components/feedback/error-toast';
import type { MediaPlanPreviewCardProps } from '../preview-card-registry';
import { ProposalSummarySection } from '../proposal-summary-section';
import { CommitCardFrame, type CommitOutcome } from '@/features/ai/shared/commit-card-frame';

export function MetaMediaPlanPreviewCard({
  data,
  strategyId,
  lineItemName,
  onFieldsEdited,
  onCreated,
  onCreatedLinks,
  nextPlatform,
  onRequestNewPlatform,
  toolCallId,
  externalOutcome,
  onResolve
}: MediaPlanPreviewCardProps): React.JSX.Element {
  // proposeMeta orders entities [campaign, ad_set] per group; every group is created (no existing
  // mode), so each is editable in place — names, lifetime budget, and the ad set flight.
  const initialGroups = toGroups(data.entities);
  const [groups, setGroups] = React.useState<MetaGroup[]>(() => cloneGroups(initialGroups));
  const initialGroupsRef = React.useRef(cloneGroups(initialGroups));

  const [outcome, setOutcome] = React.useState<CommitOutcome | null>(null);
  const [campaignSuccessLinks, setCampaignSuccessLinks] = React.useState<LinkData[]>([]);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [createWarnings, setCreateWarnings] = React.useState<string[]>([]);
  const [notCreated, setNotCreated] = React.useState<string[]>([]);
  const [editing, setEditing] = React.useState(false);

  const confirmMutation = trpc.metaConfirmMediaPlan.useMutation();
  const isPending = confirmMutation.isPending;

  async function handleCreate() {
    setErrorMsg(null);
    if (groups.length === 0) {
      setErrorMsg('Preview is missing its campaign + ad set pairs; ask in chat to re-propose.');
      return;
    }
    if (!toolCallId || !onResolve) {
      setErrorMsg(
        'This preview is not attached to a createMetaMediaPlan call; re-propose in chat.'
      );
      return;
    }
    try {
      const res = await confirmMutation.mutateAsync({
        execution_line_item_id: data.execution_line_item_id,
        strategy_id: strategyId,
        platform_id: data.platform_id,
        tool_call_id: toolCallId,
        account_id: data.account_id ?? '',
        entities: toEntities(groups)
      });
      setOutcome({ status: 'applied', summary: 'Media plan created in Meta' });
      setEditing(false);
      setCreateWarnings(res.result?.warnings ?? []);
      setNotCreated(res.result?.not_created ?? []);
      const links = campaignLinksFromResolved(res.result?.resolved_entities ?? [], data.account_id);
      setCampaignSuccessLinks(links);
      onCreated?.();
      onCreatedLinks?.(links);
      await onResolve({
        status: res.status,
        media_plans: res.result?.media_plans,
        resolved_entities: res.result?.resolved_entities,
        warnings: res.result?.warnings,
        not_created: res.result?.not_created
      });
    } catch (err) {
      const partial = extractPartialFailureDetail(err);
      const msg = partial
        ? formatPartialFailureMessage(partial)
        : err instanceof Error
          ? err.message
          : 'Failed to create media plan';
      setOutcome({ status: 'failed', summary: msg });
      showErrorToast(msg);
      // The agent gets the error so it can fix a fixable input and re-propose a corrected card.
      await onResolve({ status: 'failed', error: msg });
    }
  }

  async function handleCancel() {
    setOutcome({ status: 'cancelled' });
    if (onResolve) await onResolve({ status: 'cancelled' });
  }

  function handleToggleEdit() {
    if (editing && onFieldsEdited) {
      const diff = computeEditDiff(initialGroupsRef.current, groups);
      if (diff) onFieldsEdited(diff);
    }
    setEditing(!editing);
  }

  function patchGroup(index: number, patch: Partial<MetaGroup>) {
    setGroups(prev => prev.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  }

  const applied = outcome?.status === 'applied';
  const locked = isPending || outcome !== null || Boolean(externalOutcome);
  const campaignCount = groups.length;

  return (
    <>
      <CommitCardFrame
        tier="live"
        badgeLabel="Writes to Meta"
        title="Create media plan in Meta"
        consequence={
          `Creates ${campaignCount} campaign${campaignCount === 1 ? '' : 's'} + ${campaignCount} ad ` +
          `set${campaignCount === 1 ? '' : 's'} in Meta, each campaign its own media buy + media plan — ` +
          'created paused; nothing serves until ads are added and enabled.'
        }
        confirmLabel="Create in Meta"
        confirmDisabled={editing}
        isPending={isPending}
        outcome={outcome}
        externalOutcome={externalOutcome ?? null}
        toolCallId={toolCallId}
        errorMsg={errorMsg}
        onConfirm={handleCreate}
        onCancel={handleCancel}>
        {editing ? (
          <div className="mb-3 rounded-lg bg-amber-400 px-4 py-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              Edit Mode — modify fields below, then click Done Editing
            </div>
          </div>
        ) : null}

        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Linking to: <span className="font-medium text-gray-700">{lineItemName}</span>
          </div>
          <div className="flex items-center gap-3">
            {data.account_id ? (
              <div className="text-xs text-gray-500">
                Ad account: <span className="font-medium text-gray-700">{data.account_id}</span>
              </div>
            ) : null}
            {!locked ? (
              <Button onClick={handleToggleEdit} variant="outline" size="sm">
                {editing ? 'Done Editing' : 'Edit'}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
          <p className="font-semibold">All entities are created PAUSED.</p>
          <p className="mt-1">
            Campaign + ad set shells only: add ads (and any detailed targeting) in Ads Manager
            before anything can serve.
          </p>
        </div>

        {data.notices?.map((notice, i) => {
          const view = describeNotice(notice);
          return view ? <NoticeBanner key={`notice-${i}`} {...view} /> : null;
        })}

        {data.summary?.length ? <ProposalSummarySection summary={data.summary} /> : null}

        <div className="space-y-3">
          <div className="text-xs font-semibold text-gray-600">
            {campaignCount} campaign{campaignCount === 1 ? '' : 's'} — each becomes its own media
            plan under the line item.
          </div>
          {groups.map((group, i) => (
            <CampaignGroupSection
              key={`group-${i}`}
              index={i}
              group={group}
              editing={editing}
              locked={locked}
              onChange={patch => patchGroup(i, patch)}
            />
          ))}
        </div>

        {groups.length > 0 ? (
          <AppliedAutomaticallySection
            specialAdCategories={groups[0].campaign.special_ad_categories}
          />
        ) : null}
      </CommitCardFrame>

      {applied ? (
        <div className="mt-2 space-y-2">
          {campaignSuccessLinks.length > 0 || (nextPlatform && onRequestNewPlatform) ? (
            <div className="flex flex-wrap items-center gap-3 px-1">
              {campaignSuccessLinks.map((link, i) => (
                <a
                  key={`camp-url-${i}`}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary hover:underline">
                  {link.label} ↗
                </a>
              ))}
              {nextPlatform && onRequestNewPlatform ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRequestNewPlatform(nextPlatform.id)}>
                  Create another for {nextPlatform.name}
                </Button>
              ) : null}
            </div>
          ) : null}
          {createWarnings.length > 0 ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold">
                Created with warnings — Meta differs from what was sent
              </p>
              {createWarnings.map((warning, i) => (
                <p key={`warning-${i}`} className="mt-1">
                  {warning}
                </p>
              ))}
            </div>
          ) : null}
          {notCreated.length > 0 ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
              <p className="font-semibold">Still to set up in Ads Manager</p>
              <ul className="mt-1 list-disc pl-4">
                {notCreated.map((item, i) => (
                  <li key={`not-created-${i}`} className="mt-0.5">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

type MetaGroup = {
  campaign: CreateMetaCampaignPayload;
  adSet: CreateMetaAdSetPayload;
};

// Pairs the flat entity list into [campaign, ad_set] groups. Returns [] when the list isn't clean
// create-mode pairs in that order (the backend's validation is the source of truth) so the card
// reports the problem instead of rendering fields against the wrong entity.
function toGroups(entities: EntityDecision[]): MetaGroup[] {
  if (entities.length === 0 || entities.length % 2 !== 0) return [];
  const groups: MetaGroup[] = [];
  for (let i = 0; i + 2 <= entities.length; i += 2) {
    const campaign = entities[i];
    const adSet = entities[i + 1];
    if (
      campaign.mode !== 'create' ||
      campaign.entityType !== 'campaign' ||
      adSet.mode !== 'create' ||
      adSet.entityType !== 'ad_set'
    ) {
      return [];
    }
    groups.push({
      campaign: campaign.payload as CreateMetaCampaignPayload,
      adSet: adSet.payload as CreateMetaAdSetPayload
    });
  }
  return groups;
}

function cloneGroups(groups: MetaGroup[]): MetaGroup[] {
  return groups.map(g => ({ campaign: { ...g.campaign }, adSet: { ...g.adSet } }));
}

function toEntities(groups: MetaGroup[]): EntityDecision[] {
  return groups.flatMap(g => [
    { mode: 'create' as const, entityType: 'campaign', payload: g.campaign },
    { mode: 'create' as const, entityType: 'ad_set', payload: g.adSet }
  ]);
}

function campaignLinksFromResolved(resolved: ResolvedEntity[], accountId?: string): LinkData[] {
  const ancestry = accountId
    ? new Map([['account', { platformEntityId: accountId, internalId: '' }]])
    : undefined;
  return resolved
    .filter(r => r.entityType === 'campaign')
    .map(r => ({
      url: r.url ?? buildMetaEntityUrl('campaign', r.platform_entity_id, ancestry),
      label: r.entity_name ?? `Campaign ${r.platform_entity_id}`
    }));
}

type NoticeView = { tone: 'warn' | 'info'; title: string; body: React.ReactNode };

function describeNotice(notice: MediaPlanPreviewNotice): NoticeView | null {
  switch (notice.type) {
    case 'start_date_adjusted':
      return {
        tone: 'warn',
        title: 'Start time adjusted',
        body: (
          <>
            The plan&apos;s start ({notice.original_start_date}) is in the past. Meta can&apos;t
            backdate delivery, so the ad sets start{' '}
            <span className="font-semibold">{notice.adjusted_start_date}</span> instead. Edit the ad
            set start below if you want a different time.
          </>
        )
      };
    case 'geo_target_unresolved':
      return {
        tone: 'warn',
        title: 'Geo target not recognized',
        body: (
          <>
            Meta could not match{' '}
            <span className="font-semibold">&quot;{notice.location_text}&quot;</span> to a
            targetable location, so it was left out. Ask in chat with a more specific name (e.g. add
            the state) to regenerate this preview, or add it in Ads Manager after creation.
          </>
        )
      };
    case 'geo_target_ambiguous':
      return {
        tone: 'warn',
        title: 'Geo target matched multiple locations',
        body: (
          <>
            &quot;{notice.location_text}&quot; will target{' '}
            <span className="font-semibold">{notice.resolved_display_name}</span>. Other matches:{' '}
            {notice.alternatives.join('; ')}. If this is the wrong one, ask in chat with a more
            specific name to regenerate this preview.
          </>
        )
      };
    case 'budget_mismatch':
      return {
        tone: 'warn',
        title: "Campaign budgets don't add up to the line item",
        body: (
          <>
            The campaign budgets total{' '}
            <span className="font-semibold">${notice.campaigns_total.toFixed(2)}</span> but the line
            item media budget is{' '}
            <span className="font-semibold">${notice.line_item_price.toFixed(2)}</span> (
            {notice.difference >= 0 ? '+' : ''}
            {notice.difference.toFixed(2)}). Adjust the split so they reconcile.
          </>
        )
      };
    case 'campaign_name_duplicate':
      return {
        tone: 'warn',
        title: 'Campaign name already in use',
        body: (
          <>
            A campaign named <span className="font-semibold">&quot;{notice.name}&quot;</span>{' '}
            already exists in this ad account (campaign {notice.existing_campaign_id}). Edit the
            campaign name below to make it unique before creating.
          </>
        )
      };
    case 'campaign_name_unverified':
      return {
        tone: 'warn',
        title: 'Campaign name not verified',
        body: (
          <>
            We couldn&apos;t check whether{' '}
            <span className="font-semibold">&quot;{notice.name}&quot;</span> is already in use in
            this ad account. The name is correct either way; if you know it clashes, edit it below.
          </>
        )
      };
    case 'special_ad_category_age_min_dropped':
      return {
        tone: 'warn',
        title: 'Minimum age dropped',
        body: (
          <>
            {notice.special_ad_categories.join(', ')} is declared, so Meta restricts audience
            targeting: the requested minimum age of{' '}
            <span className="font-semibold">{notice.requested_age_min}</span> was dropped and
            Meta&apos;s own floor applies.
          </>
        )
      };
    case 'pacing_blocks_unsupported':
      return {
        tone: 'info',
        title: 'Pacing blocks are not expressible in Meta',
        body: (
          <>
            This line item paces in {notice.block_count} blocks, but a Meta campaign holds one
            lifetime budget that Meta paces itself. Rebalance the campaign budgets in Ads Manager as
            the blocks change.
          </>
        )
      };
    default:
      return null;
  }
}

function NoticeBanner({ tone, title, body }: NoticeView) {
  const toneClass =
    tone === 'warn'
      ? 'border-amber-300 bg-amber-50 text-amber-900'
      : 'border-blue-200 bg-blue-50 text-blue-900';
  return (
    <div className={`mb-4 rounded-lg border p-3 text-xs ${toneClass}`}>
      <p className="font-semibold">{title}</p>
      <p className="mt-1">{body}</p>
    </div>
  );
}

function CampaignGroupSection({
  index,
  group,
  editing,
  locked,
  onChange
}: {
  index: number;
  group: MetaGroup;
  editing: boolean;
  locked: boolean;
  onChange: (patch: Partial<MetaGroup>) => void;
}) {
  const { campaign, adSet } = group;
  const objective = objectiveLabel(campaign.objective);
  const geo = formatGeoLocations(adSet.targeting?.geo_locations);
  if (!editing) {
    return (
      <div className="rounded-lg border border-gray-200 p-3">
        <h4 className="mb-2 text-xs font-semibold text-gray-600">
          Campaign {index + 1} — {objective}
        </h4>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <ViewField label="Campaign" value={campaign.name} />
          <ViewField
            label="Lifetime budget ($)"
            value={`$${campaign.lifetime_budget_dollars.toFixed(2)}`}
          />
          <ViewField label="Ad set" value={adSet.name} />
          <ViewField label="Performance goal" value={adSet.optimization_goal} />
          <ViewField label="Start" value={adSet.start_time} />
          <ViewField label="End" value={adSet.end_time} />
          <ViewField label="Page" value={adSet.promoted_object?.page_id ?? '—'} />
          <ViewField
            label="Audience"
            value={`Age ${adSet.targeting?.age_min ?? '—'}+, all genders, all languages`}
          />
          {geo ? <ViewField label="Geo" value={geo} /> : null}
        </dl>
      </div>
    );
  }
  return (
    <div className="rounded-lg border p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-600">
        Campaign {index + 1} — {objective}
      </h4>
      <div className="grid grid-cols-2 gap-2">
        <EditField label="Campaign name">
          <Input
            value={campaign.name}
            onChange={e => onChange({ campaign: { ...campaign, name: e.target.value } })}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Lifetime budget ($)">
          <Input
            type="number"
            step="0.01"
            value={campaign.lifetime_budget_dollars}
            onChange={e =>
              onChange({
                campaign: {
                  ...campaign,
                  lifetime_budget_dollars: parseFloat(e.target.value) || 0
                }
              })
            }
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="Ad set name">
          <Input
            value={adSet.name}
            onChange={e => onChange({ adSet: { ...adSet, name: e.target.value } })}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        {/* The performance goal can't change after the ad set is saved, and here it follows the
            objective picked on the configuration card — re-propose to change it. */}
        <EditField label="Performance goal (edit via card)">
          <Input value={adSet.optimization_goal} disabled className="h-9 text-sm" />
        </EditField>
        <EditField label="Start (account timezone)">
          <Input
            value={adSet.start_time}
            onChange={e => onChange({ adSet: { ...adSet, start_time: e.target.value } })}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        <EditField label="End (account timezone)">
          <Input
            value={adSet.end_time}
            onChange={e => onChange({ adSet: { ...adSet, end_time: e.target.value } })}
            disabled={locked}
            className="h-9 text-sm"
          />
        </EditField>
        {/* Geo is not editable here: changes go through chat (re-propose) so named locations are
            re-resolved to Meta geo keys at preview time. */}
        {geo ? (
          <EditField label="Geo (edit via chat)">
            <Input value={geo} disabled className="h-9 text-sm" />
          </EditField>
        ) : null}
      </div>
    </div>
  );
}

// The trader-spec constants set server-side on every Meta campaign / ad set, shown so the user
// confirms with eyes open. Campaign Spend Limit is called out as deliberately unused so QA does not
// look for it.
function AppliedAutomaticallySection({ specialAdCategories }: { specialAdCategories?: string[] }) {
  return (
    <div className="mt-4 rounded-lg bg-gray-50 p-3">
      <h4 className="mb-2 text-xs font-semibold text-gray-500">APPLIED AUTOMATICALLY</h4>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <ViewField label="Buying type" value="Auction" />
        <ViewField label="Budget" value="Advantage campaign budget (lifetime, campaign-level)" />
        <ViewField label="Campaign spend limit" value="Not set (N/A for this flow)" />
        <ViewField label="Bid strategy" value="Highest volume (lowest cost, no cap)" />
        <ViewField label="Billing" value="Impressions" />
        <ViewField
          label="Placements"
          value="Facebook Feed, Facebook Profile Feed, Instagram Feed, Instagram Profile Feed"
        />
        <ViewField label="Advantage+ audience" value="Off" />
        <ViewField
          label="Special ad categories"
          value={specialAdCategories?.length ? specialAdCategories.join(', ') : 'None declared'}
        />
      </dl>
      <p className="mt-2 text-xs text-gray-500">
        Manual placements are feeds only — anything beyond feeds raises CPM and is flagged as a
        warning after creation.
      </p>
    </div>
  );
}

function objectiveLabel(objective: CreateMetaCampaignPayload['objective']): string {
  return objective === 'OUTCOME_AWARENESS' ? 'Awareness' : 'Traffic';
}

function formatGeoLocations(geo: MetaGeoLocations | undefined): string | null {
  if (!geo) return null;
  const parts = [
    ...(geo.countries ?? []),
    ...(geo.regions ?? []).map(r => r.name ?? r.key),
    ...(geo.cities ?? []).map(c => c.name ?? c.key),
    ...(geo.zips ?? []).map(z => z.name ?? z.key)
  ];
  return parts.length > 0 ? parts.join('; ') : null;
}

function ViewField({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900 wrap-break-word">{value}</dd>
    </>
  );
}

function EditField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-0.5 block text-xs text-gray-500">{label}</label>
      {children}
    </div>
  );
}

function computeEditDiff(initial: MetaGroup[], current: MetaGroup[]): string | null {
  const changes: string[] = [];
  current.forEach((group, i) => {
    const before = initial[i];
    if (!before) return;
    const ref = `campaign ${i + 1}`;
    if (before.campaign.name !== group.campaign.name)
      changes.push(`${ref} name to "${group.campaign.name}"`);
    if (before.campaign.lifetime_budget_dollars !== group.campaign.lifetime_budget_dollars)
      changes.push(`${ref} lifetime budget to $${group.campaign.lifetime_budget_dollars}`);
    if (before.adSet.name !== group.adSet.name)
      changes.push(`${ref} ad set name to "${group.adSet.name}"`);
    if (before.adSet.start_time !== group.adSet.start_time)
      changes.push(`${ref} start to ${group.adSet.start_time}`);
    if (before.adSet.end_time !== group.adSet.end_time)
      changes.push(`${ref} end to ${group.adSet.end_time}`);
  });
  if (changes.length === 0) return null;
  return `Changed ${changes.join(', ')}`;
}
